import { Button } from "@/components/ui/button";
import { scrollToForm, trackEvent } from "@/lib/tracking";
import { ArrowUpRight, Repeat, Send, BarChart3 } from "lucide-react";
import { FaGoogle, FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";

function HeroDashboard() {
  return (
    <div className="relative">
      {/* Floating background blobs */}
      <div aria-hidden className="pointer-events-none absolute -inset-8 -z-10">
        <div className="absolute top-0 -left-4 h-40 w-40 rounded-full bg-[#4285f4]/20 blur-3xl float-y" />
        <div className="absolute bottom-0 right-0 h-44 w-44 rounded-full bg-[#fbbc04]/30 blur-3xl float-y-2" />
        <div className="absolute top-1/2 right-10 h-32 w-32 rounded-full bg-[#34a853]/20 blur-3xl float-y-3" />
        <div className="absolute -bottom-6 left-10 h-32 w-32 rounded-full bg-[#ea4335]/20 blur-3xl float-y" />
      </div>

      <div
        data-testid="hero-dashboard"
        className="relative w-full rounded-3xl border border-zinc-200 bg-white p-5 sm:p-6 shadow-[0_20px_70px_-20px_rgba(0,0,0,0.15)]"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#34a853] pulse-dot" />
            <span className="text-[11px] tracking-[0.18em] uppercase text-zinc-500 font-mono">
              Live · campaign overview
            </span>
          </div>
          <span className="text-[11px] text-zinc-400 font-mono">REKISA / ZA</span>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <ChannelTile color="#4285f4" icon={<FaGoogle />} label="Google Ads" value="CPL R164" trend="+18%" />
          <ChannelTile color="#ea4335" icon={<><FaFacebookF /><FaInstagram /></>} label="Meta Ads" value="CTR 3.4%" trend="+22%" />
          <ChannelTile color="#0a66c2" icon={<FaLinkedinIn />} label="LinkedIn" value="CAC R940" trend="+9%" />
        </div>

        <div className="mt-5 rounded-2xl border border-zinc-200 bg-zinc-50 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Funnel · 30D</span>
            <span className="text-xs text-zinc-700 font-semibold">142 qualified leads</span>
          </div>
          <FunnelBar label="Clicks" value="4,821" widthPct={100} delay={0} color="#4285f4" />
          <FunnelBar label="Landing visits" value="4,418" widthPct={91} delay={120} color="#34a853" />
          <FunnelBar label="Lead capture" value="312" widthPct={42} delay={240} color="#fbbc04" />
          <FunnelBar label="Follow-up engaged" value="206" widthPct={28} delay={360} color="#ea4335" />
          <FunnelBar label="Qualified" value="142" widthPct={19} delay={480} color="#1a73e8" accent />
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <SystemPill icon={<Repeat className="h-3.5 w-3.5" />} label="Remarketing" />
          <SystemPill icon={<Send className="h-3.5 w-3.5" />} label="Follow-up" />
          <SystemPill icon={<BarChart3 className="h-3.5 w-3.5" />} label="Tracking" />
        </div>
      </div>

      {/* Floating mini-cards */}
      <div
        aria-hidden
        className="hidden md:flex absolute -left-8 top-20 items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-3 py-2 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.15)] float-y"
      >
        <div className="h-8 w-8 rounded-full bg-[#4285f4]/10 flex items-center justify-center text-[#4285f4]">
          <FaGoogle />
        </div>
        <div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">CTR</div>
          <div className="text-sm font-bold text-zinc-900">+22%</div>
        </div>
      </div>
      <div
        aria-hidden
        className="hidden md:flex absolute -right-6 bottom-20 items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-3 py-2 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.15)] float-y-2"
      >
        <div className="h-8 w-8 rounded-full bg-[#34a853]/10 flex items-center justify-center text-[#34a853]">
          <BarChart3 className="h-4 w-4" />
        </div>
        <div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">Leads</div>
          <div className="text-sm font-bold text-zinc-900">142</div>
        </div>
      </div>
    </div>
  );
}

function ChannelTile({ icon, label, value, trend, color }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-3 hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.15)] transition-shadow">
      <div className="flex items-center justify-between">
        <div
          className="h-7 w-7 rounded-md flex items-center justify-center text-[12px] gap-0.5"
          style={{ background: `${color}15`, color }}
        >
          {icon}
        </div>
        <span className="text-[10px] text-[#34a853] font-mono font-semibold">{trend}</span>
      </div>
      <div className="mt-2 text-[10px] text-zinc-500 font-mono uppercase tracking-wider">{label}</div>
      <div className="text-sm text-zinc-900 font-bold mt-0.5">{value}</div>
    </div>
  );
}

function FunnelBar({ label, value, widthPct, delay = 0, color = "#1a73e8", accent }) {
  return (
    <div className="mb-2.5 last:mb-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-zinc-600">{label}</span>
        <span className="text-xs text-zinc-900 font-mono font-semibold">{value}</span>
      </div>
      <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full bar-grow ${accent ? "shadow-[0_0_12px_rgba(26,115,232,0.4)]" : ""}`}
          style={{ width: `${widthPct}%`, background: color, animationDelay: `${delay}ms` }}
        />
      </div>
    </div>
  );
}

function SystemPill({ icon, label }) {
  return (
    <div className="flex items-center justify-center gap-1.5 rounded-full border border-zinc-200 bg-zinc-50 py-1.5 text-[11px] text-zinc-700 font-semibold">
      {icon}
      {label}
    </div>
  );
}

export default function Hero() {
  const handleCta = () => {
    trackEvent("cta_click", { location: "hero" });
    scrollToForm();
  };

  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 pb-20 sm:pt-36 sm:pb-28 overflow-hidden bg-paid-wash"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-0 left-0 right-0 h-[500px] bg-gradient-to-b from-[#4285f4]/5 via-transparent to-transparent" />
        <div
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.04) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse at top, black 0%, transparent 65%)",
          }}
        />
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3.5 py-1.5 text-[11px] tracking-[0.14em] uppercase text-zinc-700 font-semibold shadow-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-[#34a853] pulse-dot" />
            Paid Ads · Built for South African Businesses
          </div>

          <h1
            data-testid="hero-h1"
            className="font-display mt-6 text-[48px] leading-[0.98] sm:text-[72px] lg:text-[88px] font-black tracking-[-0.035em] text-zinc-900"
          >
            Get More Leads & Sales{" "}<br />
            With Rekisa.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-7 text-lg sm:text-xl text-zinc-600 max-w-2xl leading-relaxed"
          >
            We'll help you with your Google, Meta and LinkedIn ads — with remarketing, automated follow-up, tracking and monthly optimisation.
          </p>

          <p
            data-testid="hero-microcopy"
            className="mt-3 text-sm text-zinc-500 max-w-2xl"
          >
            Best for businesses that want more enquiries, bookings, consultations, registrations, online sales, or B2B leads.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-5">
            <Button
              onClick={handleCta}
              data-testid="hero-cta-request-quote"
              className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full h-14 px-7 text-base font-semibold shadow-[0_8px_24px_rgba(26,115,232,0.35)] hover:shadow-[0_12px_32px_rgba(26,115,232,0.45)] transition-all"
            >
              Request a Quote
              <ArrowUpRight className="ml-1 h-5 w-5" />
            </Button>
            <div className="text-xs text-zinc-500 max-w-[260px] leading-relaxed">
              Paid ads setup, tracking, optimisation, and reporting for South African businesses.
            </div>
          </div>

          <div
            data-testid="hero-trust-strip"
            className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-mono"
          >
            <span className="flex items-center gap-2"><FaGoogle className="text-[#4285f4]" /> Google Ads</span>
            <span className="flex items-center gap-2"><FaFacebookF className="text-[#1877f2]" /> Meta</span>
            <span className="flex items-center gap-2"><FaInstagram className="text-[#e1306c]" /> Instagram</span>
            <span className="flex items-center gap-2"><FaLinkedinIn className="text-[#0a66c2]" /> LinkedIn</span>
            <span className="flex items-center gap-2"><Repeat className="h-3.5 w-3.5 text-zinc-500" /> Remarketing</span>
          </div>
        </div>

        <div className="reveal" style={{ animationDelay: "120ms" }}>
          <HeroDashboard />
        </div>
      </div>
    </section>
  );
}
